import { api } from './client';

// Types matching backend
export interface Project {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  center_lat: number;
  center_lng: number;
  zoom_level: number;
  is_public: number;
  share_token: string | null;
  created_at: string;
  updated_at: string;
}

export interface Layer {
  id: string;
  projectId: string;
  name: string;
  fileKey: string;
  fileType: string;
  geojsonKey: string | null;
  style: LayerStyle;
  zIndex: number;
  isVisible: boolean;
  bbox: { north: number; south: number; east: number; west: number } | null;
  createdAt: string;
  updatedAt: string;
}

export interface LayerStyle {
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  fillColor?: string;
  fillOpacity?: number;
  labelField?: string;
  zIndex?: number;
}

export interface ParcelWithPrice {
  id: string;
  parcelCode: string | null;
  sheetNumber: string | null;
  parcelNumber: string | null;
  address: string | null;
  landType: string;
  areaSqm: number;
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: any } | null;
  bbox: { north: number; south: number; east: number; west: number } | null;
  latestPrice: number | null;
  priceDate: string | null;
}

export interface SpatialQueryResult {
  projects: Array<{
    id: string;
    name: string;
    layers: Array<{
      id: string;
      name: string;
      geojsonKey: string;
      style: LayerStyle;
      zIndex: number;
    }>;
  }>;
  parcels: ParcelWithPrice[];
}

export const projectsApi = {
  list: () => api<{ items: Project[] }>('/api/projects'),
  get: (id: string) => api<Project>(`/api/projects/${id}`),
  create: (data: { name: string; description?: string; centerLat: number; centerLng: number; zoomLevel: number }) =>
    api<Project>('/api/projects', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string; description: string; is_public: boolean }>) =>
    api<Project>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  publish: (id: string) =>
    api<{ is_public: boolean }>(`/api/projects/${id}/publish`, { method: 'POST' }),
  share: (id: string) =>
    api<{ share_token: string; share_url: string }>(`/api/projects/${id}/share`, { method: 'POST' }),
};

export const layersApi = {
  listForProject: (projectId: string) =>
    api<{ items: Layer[] }>(`/api/projects/${projectId}/layers`),
  get: (id: string) => api<Layer>(`/api/layers/${id}`),
  update: (id: string, data: { style?: LayerStyle; isVisible?: boolean; name?: string; zIndex?: number }) =>
    api<Layer>(`/api/layers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => api<{ success: boolean }>(`/api/layers/${id}`, { method: 'DELETE' }),
  getGeoJSONUrl: (id: string) => `${import.meta.env.VITE_API_BASE || ''}/api/layers/${id}/geojson`,
};

export const parcelsApi = {
  list: () => api<{ items: ParcelWithPrice[] }>('/api/parcels'),
  get: (id: string) => api<ParcelWithPrice>(`/api/parcels/${id}`),
};

export const spatialApi = {
  query: (lat: number, lng: number) =>
    api<SpatialQueryResult>('/api/spatial/query', {
      method: 'POST',
      body: JSON.stringify({ lat, lng }),
      skipAuth: true,  // public endpoint
    }),
};
